import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, ArrowRight, Phone, Mail, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import SEOHead from './SEOHead';
import RequestQuoteDialog from './RequestQuoteDialog';
import FloatingWhatsapp from './FloatingWhatsapp';
import { COMPANY, organizationJsonLd, localBusinessJsonLd } from '../content/seo';
import PublicHeader from './PublicHeader';

/**
 * Generic SEO landing template used by industries, product categories,
 * services, and locations. Highly data-driven — the parent supplies the
 * `entity` object and `kind` label.
 */
export default function SEOLandingTemplate({
  entity,
  kind,          // 'industry' | 'product' | 'service' | 'location'
  pathPrefix,    // '/industries' | '/products' | '/services' | '/location'
  breadcrumb,    // 'Industries' | 'Products' | 'Services' | 'Locations'
  siblings = [], // for related-links section
}) {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const path = `${pathPrefix}/${entity.slug}`;

  const title = `${entity.name} — ${entity.tagline || 'Bison Food Solutions'}`;
  const description = entity.hero;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: COMPANY.site },
      { '@type': 'ListItem', position: 2, name: breadcrumb, item: `${COMPANY.site}${pathPrefix}` },
      { '@type': 'ListItem', position: 3, name: entity.name, item: `${COMPANY.site}${path}` },
    ],
  };
  const faqJsonLd = entity.faqs?.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entity.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  const jsonLd = [breadcrumbJsonLd, organizationJsonLd()];
  if (faqJsonLd) jsonLd.push(faqJsonLd);
  if (kind === 'location') jsonLd.push(localBusinessJsonLd());

  const bullets = entity.benefits || entity.highlights || [];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead title={title} description={description} path={path} jsonLd={jsonLd} />
      <PublicHeader onQuote={() => setQuoteOpen(true)} />

      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="max-w-6xl mx-auto px-4 pt-6 text-sm text-neutral-500">
        <ol className="flex items-center gap-2 flex-wrap" itemScope itemType="https://schema.org/BreadcrumbList">
          <li>
            <Link to="/" className="hover:text-emerald-700">Home</Link>
          </li>
          <ChevronRight className="w-3 h-3" />
          <li>
            <Link to={pathPrefix} className="hover:text-emerald-700">{breadcrumb}</Link>
          </li>
          <ChevronRight className="w-3 h-3" />
          <li aria-current="page" className="text-neutral-900 font-medium">{entity.name}</li>
        </ol>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="text-emerald-700 font-semibold uppercase tracking-wide text-sm">
            {breadcrumb} · {entity.name}
          </p>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-neutral-900 mt-3 leading-tight">
            {entity.tagline || entity.name}
          </h1>
          <p className="text-lg text-neutral-600 mt-4 leading-relaxed">{entity.hero}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button
              size="lg"
              onClick={() => setQuoteOpen(true)}
              data-testid="cta-request-quote"
              className="bg-emerald-700 hover:bg-emerald-800"
            >
              Request a Quote <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <a href={entity.ctaLink || '/shop'}>
              <Button size="lg" variant="outline" data-testid="cta-shop">
                {entity.ctaLabel || 'Shop Online'}
              </Button>
            </a>
          </div>
        </div>
        <div className="aspect-video rounded-xl bg-gradient-to-br from-emerald-100 via-emerald-50 to-amber-50 shadow-lg flex items-center justify-center text-6xl font-serif text-emerald-900/40 select-none">
          {entity.name.charAt(0)}
        </div>
      </section>

      {/* Bullets */}
      {bullets.length > 0 && (
        <section className="bg-neutral-50 border-y border-neutral-200">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <h2 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900">
              Why {COMPANY.name.split(' ')[0]} for {entity.name}
            </h2>
            <ul className="grid md:grid-cols-2 gap-4 mt-6">
              {bullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <span className="text-neutral-700 leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* FAQs */}
      {entity.faqs?.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-14">
          <h2 className="text-2xl md:text-3xl font-serif font-bold text-neutral-900">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-4">
            {entity.faqs.map((f, i) => (
              <details key={i} className="group border border-neutral-200 rounded-lg p-4 open:bg-neutral-50">
                <summary className="cursor-pointer font-semibold text-neutral-900 flex justify-between items-center">
                  {f.q}
                  <ChevronRight className="w-4 h-4 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-neutral-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {siblings.length > 0 && (
        <section className="bg-white border-t border-neutral-200">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <h2 className="text-2xl font-serif font-bold text-neutral-900">
              Explore more {breadcrumb.toLowerCase()}
            </h2>
            <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3 mt-6">
              {siblings.slice(0, 8).map((s) => (
                <Link
                  key={s.slug}
                  to={`${pathPrefix}/${s.slug}`}
                  className="p-4 rounded-lg border border-neutral-200 hover:border-emerald-700 hover:bg-emerald-50/40 transition-colors text-neutral-800 font-medium"
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="bg-emerald-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-serif font-bold">Ready to simplify your food procurement?</h2>
            <p className="text-emerald-100 mt-3 leading-relaxed">
              Talk to our sales team about volumes, delivery windows and pricing tailored to your business.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Button size="lg" onClick={() => setQuoteOpen(true)} className="bg-amber-400 text-emerald-900 hover:bg-amber-300">
              Request Quote
            </Button>
            <a href={`tel:${COMPANY.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <Phone className="w-4 h-4 mr-1" /> {COMPANY.phoneDisplay}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <PublicFooter />
      <FloatingWhatsapp />
      <RequestQuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} defaultInterest={entity.name} />
    </div>
  );
}

export function PublicFooter() {
  return (
    <footer className="bg-neutral-900 text-neutral-300">
      <div className="max-w-6xl mx-auto px-4 py-14 grid md:grid-cols-4 gap-8">
        <div>
          <h3 className="font-serif text-2xl text-white">{COMPANY.name.split(' ').slice(0, 2).join(' ')}</h3>
          <p className="text-sm mt-2 text-neutral-400">{COMPANY.slogan}</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3">Contact</h4>
          <p className="text-sm"><Phone className="inline w-3 h-3 mr-1" /> {COMPANY.phoneDisplay}</p>
          <p className="text-sm"><Mail className="inline w-3 h-3 mr-1" /> {COMPANY.email}</p>
          <p className="text-sm mt-1">{COMPANY.address.street}, {COMPANY.address.city}</p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3">Explore</h4>
          <ul className="text-sm space-y-1">
            <li><Link to="/products" className="hover:text-white">Products</Link></li>
            <li><Link to="/industries/hotels" className="hover:text-white">Industries</Link></li>
            <li><Link to="/services" className="hover:text-white">Services</Link></li>
            <li><Link to="/shop" className="hover:text-white">Online Shop</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-3">Hours</h4>
          <p className="text-sm">{COMPANY.hours}</p>
        </div>
      </div>
      <div className="border-t border-neutral-800 py-4 text-center text-xs text-neutral-500">
        © {new Date().getFullYear()} {COMPANY.name}. All rights reserved.
      </div>
    </footer>
  );
}
